import type { GuidedAgeUnit } from '../types/guided-mode.types'
import { isValidAge, NEWBORN_MAX_DAYS } from './assessment-selection'

interface AgeSummaryOptions {
  age: number | undefined
  ageUnit: GuidedAgeUnit
  ageMinimum: number
  ageMaximum: number
  ageInvalid: boolean
}

type FormatAgePart = (value: number, unit: 'day' | 'month' | 'year') => string

export function formatAgeSummary(
  { age, ageUnit, ageMinimum, ageMaximum, ageInvalid }: AgeSummaryOptions,
  format: FormatAgePart,
): string {
  if (ageInvalid || age === undefined || !isValidAge(age, ageUnit)
    || age < ageMinimum || age > ageMaximum || ageUnit === 'years') return ''
  const childMaxExclusive = ageUnit === 'months' ? 5 * 12 : 5 * 365
  if (age >= childMaxExclusive) return ''
  if (ageUnit === 'days' && age <= NEWBORN_MAX_DAYS) return format(age, 'day')

  // Without a birth date, day entries use an average month (365 / 12 days).
  const months = Math.floor(ageUnit === 'months' ? age : age * 12 / 365)
  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  // Count remaining days from the first whole day of the completed month.
  const remainingDays = ageUnit === 'days' && months < 2
    ? age - Math.ceil(months * 365 / 12)
    : 0
  return [
    years ? format(years, 'year') : '',
    remainingMonths ? format(remainingMonths, 'month') : '',
    remainingDays ? format(remainingDays, 'day') : '',
  ].filter(Boolean).join(' ')
}
